import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, ProgressBar, Modal, Badge, Table } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import LearnerNavbar from '../components/LearnerNavbar';
import '../styles/brand.css';

const SubmitAssignment = () => {
  const { assignmentId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState({
    text: '',
    files: [],
    status: 'draft'
  });
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [attemptNumber, setAttemptNumber] = useState(1);

  useEffect(() => {
    if (currentUser && userProfile?.role === 'learner') {
      loadAssignment();
    }
  }, [currentUser, userProfile, assignmentId]);

  const loadAssignment = async () => {
    try {
      const assignmentDoc = await getDoc(doc(db, 'assignments', assignmentId));
      if (!assignmentDoc.exists()) {
        setError('Assignment not found');
        return;
      }

      const assignmentData = assignmentDoc.data();
      setAssignment(assignmentData);
      
      // Check for existing submission
      const userSubmission = assignmentData.submissions?.[currentUser.uid];
      if (userSubmission) {
        setExistingSubmission(userSubmission);
        setSubmission({
          text: userSubmission.text || '',
          files: userSubmission.files || [],
          status: userSubmission.status || 'draft'
        });
        setAttemptNumber(userSubmission.attemptNumber || 1);
      }
      
    } catch (error) {
      devError('Error loading assignment:', error);
      setError('Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    // Safety check: Ensure assignment is loaded before validation
    if (!assignment) {
      setError('Please wait for assignment to load');
      return;
    }

    // Validate file types if specified
    if (assignment.allowedFileTypes && assignment.allowedFileTypes.length > 0) {
      const invalidFiles = files.filter(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        return !assignment.allowedFileTypes.includes(extension);
      });
      
      if (invalidFiles.length > 0) {
        setError(`Invalid file types. Allowed types: ${assignment.allowedFileTypes.join(', ')}`);
        return;
      }
    }
    
    // Validate file size
    const maxFileSize = assignment.maxFileSize || 10; // Default 10MB
    const oversizedFiles = files.filter(file => file.size > maxFileSize * 1024 * 1024);
    
    if (oversizedFiles.length > 0) {
      setError(`File(s) too large. Maximum size: ${maxFileSize}MB`);
      return;
    }
    
    // Validate total number of files
    const maxFiles = assignment.maxFiles || 5;
    if (selectedFiles.length + files.length > maxFiles) {
      setError(`Too many files. Maximum allowed: ${maxFiles}`);
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...files]);
    setError('');
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    const uploadedFiles = [];

    // Create user-friendly file name: UserName_AssignmentName_FileName.ext
    const getUserName = () => {
      if (userProfile?.firstName && userProfile?.lastName) {
        return `${userProfile.firstName}${userProfile.lastName}`;
      } else if (userProfile?.displayName) {
        return userProfile.displayName.replace(/\s+/g, '');
      } else if (currentUser?.displayName) {
        return currentUser.displayName.replace(/\s+/g, '');
      }
      return 'User';
    };

    const sanitizeName = (name) => {
      return name.replace(/[^a-zA-Z0-9]/g, '');
    };

    const getFileExtension = (filename) => {
      const parts = filename.split('.');
      return parts.length > 1 ? parts[parts.length - 1] : 'file';
    };

    const getFileBaseName = (filename) => {
      const parts = filename.split('.');
      return parts.slice(0, -1).join('.') || filename;
    };

    const userName = sanitizeName(getUserName());
    const idNumberPart = sanitizeName(userProfile?.idNumber || 'NoID');
    const assignmentName = sanitizeName((assignment?.title || 'Assignment').replace(/\s+/g, ''));

    for (const file of selectedFiles) {
      try {
        const fileExtension = getFileExtension(file.name);
        const fileBaseName = sanitizeName(getFileBaseName(file.name));
        const customFileName = `${userName}_${idNumberPart}_${assignmentName}_${fileBaseName}.${fileExtension}`;

        const fileRef = ref(storage, `submissions/${currentUser.uid}/${assignmentId}/${customFileName}`);
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        uploadedFiles.push({
          name: customFileName,
          originalName: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type,
          uploadedAt: new Date()
        });
      } catch (error) {
        devError('Error uploading file:', error);
        throw new Error(`Failed to upload ${file.name}`);
      }
    }

    return uploadedFiles;
  };

  const handleSaveDraft = async () => {
    try {
      setUploading(true);
      
      let uploadedFiles = [];
      if (selectedFiles.length > 0) {
        uploadedFiles = await uploadFiles();
      }
      
      const updatedSubmission = {
        ...submission,
        text: submission.text,
        files: [...submission.files, ...uploadedFiles],
        status: 'draft',
        lastSavedAt: serverTimestamp(),
        attemptNumber: attemptNumber
      };
      
      // Update assignment document
      const assignmentRef = doc(db, 'assignments', assignmentId);
      await updateDoc(assignmentRef, {
        [`submissions.${currentUser.uid}`]: updatedSubmission
      });
      
      setSubmission(updatedSubmission);
      setSelectedFiles([]);
      setSuccess('Draft saved successfully!');
      
    } catch (error) {
      devError('Error saving draft:', error);
      setError('Failed to save draft: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitAssignment = async () => {
    try {
      setUploading(true);
      
      let uploadedFiles = [];
      if (selectedFiles.length > 0) {
        uploadedFiles = await uploadFiles();
      }
      
      const nextAttemptNumber = submission.status === 'submitted'
        ? attemptNumber + 1
        : attemptNumber;

      const previousSubmissions = [
        ...(existingSubmission?.previousSubmissions || [])
      ];

      if (existingSubmission && existingSubmission.status === 'submitted') {
        previousSubmissions.push({
          ...existingSubmission,
          archivedAt: new Date()
        });
      }

      const finalSubmission = {
        ...submission,
        text: submission.text,
        files: [...submission.files, ...uploadedFiles],
        status: 'submitted',
        submittedAt: serverTimestamp(),
        attemptNumber: nextAttemptNumber,
        score: null,
        feedback: null,
        gradedAt: null,
        gradedBy: null,
        previousSubmissions
      };
      
      // Update assignment document
      const assignmentRef = doc(db, 'assignments', assignmentId);
      await updateDoc(assignmentRef, {
        [`submissions.${currentUser.uid}`]: finalSubmission
      });
      
      setSubmission(finalSubmission);
      setExistingSubmission(finalSubmission);
      setAttemptNumber(nextAttemptNumber);
      setSelectedFiles([]);
      setShowSubmitModal(false);
      setSuccess('Assignment submitted successfully!');
      
      // Stay on page so learner can view submission history
      
    } catch (error) {
      devError('Error submitting assignment:', error);
      setError('Failed to submit assignment: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isDeadlinePassed = () => {
    if (!assignment?.dueDate) return false;
    return new Date() > new Date(assignment.dueDate.toDate());
  };

  const canSubmit = () => {
    const maxAttempts = assignment?.maxAttempts || 1;
    if (isDeadlinePassed() && !assignment.allowLateSubmissions) return false;
    if (submission.status !== 'submitted') return true;
    return attemptNumber < maxAttempts;
  };

  const isExternalJotformAssignment = () =>
    assignment?.externalForm?.provider === 'jotform' && !!assignment?.externalForm?.url;

  const getStatusBadge = () => {
    switch (submission.status) {
      case 'submitted':
        return <Badge bg="success">Submitted</Badge>;
      case 'graded':
        return <Badge bg="primary">Graded</Badge>;
      case 'draft':
        return <Badge bg="secondary">Draft</Badge>;
      default:
        return <Badge bg="light" text="dark">Not Started</Badge>;
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading assignment...</p>
        </div>
      </Container>
    );
  }

  if (!userProfile || userProfile.role !== 'learner') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>Only learners can submit assignments.</p>
        </Alert>
      </Container>
    );
  }

  if (error && !assignment) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <LearnerNavbar />
      <Container className="mt-4">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Assignment Header */}
        <Card className="mb-4">
          <Card.Header>
            <Row>
              <Col md={8}>
                <h4>{assignment?.title}</h4>
                <p className="mb-0">{assignment?.description}</p>
              </Col>
              <Col md={4} className="text-end">
                <div className="mb-2">{getStatusBadge()}</div>
                {assignment?.dueDate && (
                  <div>
                    <strong>Due:</strong> {new Date(assignment.dueDate.toDate()).toLocaleString()}
                    {isDeadlinePassed() && (
                      <div className="text-danger">
                        <small>⚠️ Deadline has passed</small>
                      </div>
                    )}
                  </div>
                )}
                {assignment?.maxAttempts && (
                  <div className="text-muted small">
                    Attempts used: {Math.min(attemptNumber, assignment.maxAttempts)} / {assignment.maxAttempts}
                  </div>
                )}
              </Col>
            </Row>
          </Card.Header>
        </Card>

        {/* Assignment Details */}
        <Card className="mb-4">
          <Card.Header>
            <h5>Assignment Details</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p><strong>Max Score:</strong> {assignment?.maxScore || 100} points</p>
                <p><strong>Submission Format:</strong> {assignment?.submissionFormat || 'Online'}</p>
                {assignment?.allowedFileTypes && (
                  <p><strong>Allowed File Types:</strong> {assignment.allowedFileTypes.join(', ')}</p>
                )}
              </Col>
              <Col md={6}>
                <p><strong>Max Files:</strong> {assignment?.maxFiles || 5}</p>
                <p><strong>Max File Size:</strong> {assignment?.maxFileSize || 10}MB</p>
                <p><strong>Attempts Allowed:</strong> {assignment?.maxAttempts || 1}</p>
              </Col>
            </Row>
            
            {assignment?.instructions && (
              <div className="mt-3">
                <strong>Instructions:</strong>
                <div className="mt-2 p-3 bg-light rounded">
                  {assignment.instructions}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {isExternalJotformAssignment() && (
          <Card className="mb-4 border-primary">
            <Card.Header className="bg-light">
              <h5 className="mb-0">External Assignment Form (Jotform)</h5>
            </Card.Header>
            <Card.Body>
              <p className="mb-2">
                This assignment is completed using an external Jotform. Click the button below to complete and submit it.
              </p>
              <p className="small text-muted mb-3">
                Your facilitator will sync completions from Jotform into the LMS to track who has submitted and who has not.
              </p>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="primary"
                  onClick={() => window.open(assignment.externalForm.url, '_blank', 'noopener,noreferrer')}
                >
                  Open Jotform Assignment
                </Button>
                {assignment.externalForm.formId && (
                  <Badge bg="info" className="align-self-center">
                    Form ID: {assignment.externalForm.formId}
                  </Badge>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Rubric Display */}
        {assignment?.rubric && (
          <Card className="mb-4">
            <Card.Header>
              <h5>Grading Rubric</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Criteria</th>
                    <th>Description</th>
                    <th>Max Points</th>
                  </tr>
                </thead>
                <tbody>
                  {assignment.rubric.criteria?.map((criterion, index) => (
                    <tr key={index}>
                      <td><strong>{criterion.name}</strong></td>
                      <td>{criterion.description}</td>
                      <td>{criterion.maxPoints}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Submission Form */}
        {canSubmit() && !isExternalJotformAssignment() && (
          <Card className="mb-4">
            <Card.Header>
              <h5>Your Submission</h5>
            </Card.Header>
            <Card.Body>
              {/* Text Submission */}
              <Form.Group className="mb-3">
                <Form.Label>Written Response</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={submission.text}
                  onChange={(e) => setSubmission({...submission, text: e.target.value})}
                  placeholder="Type your response here..."
                />
              </Form.Group>

              {/* File Upload */}
              <Form.Group className="mb-3">
                <Form.Label>File Attachments</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  accept={assignment?.allowedFileTypes ? assignment.allowedFileTypes.map(type => `.${type}`).join(',') : undefined}
                />
                <Form.Text className="text-muted">
                  {assignment?.allowedFileTypes && `Allowed types: ${assignment.allowedFileTypes.join(', ')}. `}
                  Max size: {assignment?.maxFileSize || 10}MB per file. 
                  Max files: {assignment?.maxFiles || 5}
                </Form.Text>
              </Form.Group>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-3">
                  <strong>Selected Files:</strong>
                  <ul className="list-group mt-2">
                    {selectedFiles.map((file, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{file.name}</strong>
                          <br />
                          <small className="text-muted">{formatFileSize(file.size)}</small>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Existing Files */}
              {submission.files.length > 0 && (
                <div className="mb-3">
                  <strong>Previously Uploaded Files:</strong>
                  <ul className="list-group mt-2">
                    {submission.files.map((file, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{file.name}</strong>
                          <br />
                          <small className="text-muted">
                            {formatFileSize(file.size)} - 
                            Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                          </small>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => window.open(file.url, '_blank')}
                        >
                          View
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handleSaveDraft}
                  disabled={uploading || (!submission.text.trim() && selectedFiles.length === 0)}
                >
                  {uploading ? 'Saving...' : 'Save Draft'}
                </Button>
                
                <Button
                  variant="primary"
                  onClick={() => setShowSubmitModal(true)}
                  disabled={!canSubmit() || uploading || (!submission.text.trim() && selectedFiles.length === 0 && submission.files.length === 0)}
                >
                  {submission.status === 'submitted' ? 'Resubmit Assignment' : 'Submit Assignment'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Submission Results */}
        {submission.status === 'submitted' && (
          <Card className="mb-4">
            <Card.Header>
              <h5>Submission Status</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="success">
                <h6>Assignment Submitted Successfully!</h6>
                <p>Your assignment has been submitted and is awaiting review by your facilitator.</p>
                <p><strong>Submitted:</strong> {existingSubmission?.submittedAt ? 
                  new Date(existingSubmission.submittedAt.toDate()).toLocaleString() : 
                  'Just now'
                }</p>
              </Alert>
              
              {existingSubmission?.score !== null && existingSubmission?.score !== undefined && (
                <Alert variant="info">
                  <h6>Grading Results</h6>
                  <p><strong>Score:</strong> {existingSubmission.score}/{assignment?.maxScore || 100}</p>
                  {existingSubmission.feedback && (
                    <div>
                      <strong>Feedback:</strong>
                      <div className="mt-2 p-3 bg-light rounded">
                        {existingSubmission.feedback}
                      </div>
                    </div>
                  )}
                  <p><small>Graded: {new Date(existingSubmission.gradedAt?.toDate()).toLocaleString()}</small></p>
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {(existingSubmission?.previousSubmissions?.length > 0 || existingSubmission?.files?.length > 0) && (
          <Card className="mb-4">
            <Card.Header>
              <h5>Previous Submissions</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered responsive size="sm">
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th>Submitted At</th>
                    <th>Status</th>
                    <th>Files</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(existingSubmission?.previousSubmissions || []), existingSubmission].filter(Boolean).map((entry, index) => (
                    <tr key={`submission-history-${index}`}>
                      <td>{entry.attemptNumber || index + 1}</td>
                      <td>
                        {entry.submittedAt?.toDate
                          ? entry.submittedAt.toDate().toLocaleString()
                          : entry.submittedAt
                            ? new Date(entry.submittedAt).toLocaleString()
                            : '—'}
                      </td>
                      <td>{entry.status || 'submitted'}</td>
                      <td>
                        {entry.files && entry.files.length > 0 ? (
                          <ul className="mb-0">
                            {entry.files.map((file, fileIndex) => (
                              <li key={`file-${index}-${fileIndex}`}>
                                <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          'No files'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Submit Confirmation Modal */}
        <Modal show={showSubmitModal} onHide={() => setShowSubmitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Submit Assignment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to submit your assignment?</p>
            <div className="mb-3">
              <strong>Submission Summary:</strong>
              <ul>
                <li>Text response: {submission.text.trim() ? 'Yes' : 'No'}</li>
                <li>Files attached: {submission.files.length + selectedFiles.length}</li>
                <li>Attempt: {submission.status === 'submitted' ? attemptNumber + 1 : attemptNumber} of {assignment?.maxAttempts || 1}</li>
              </ul>
            </div>
            <Alert variant="warning">
              <strong>Warning:</strong> Once submitted, you cannot make changes to this assignment.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubmitModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitAssignment} disabled={uploading}>
              {uploading ? 'Submitting...' : 'Submit Assignment'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default SubmitAssignment;
